<template>
	<div class="ar_turnover">
		<div class="list_top_box">
			<select v-model="period_months" class="tbl_select right_option art_period_select">
				<option v-for="option in period_options" :key="option.value" :value="option.value">{{ option.label }}</option>
			</select>
			<h3>AR Turnover</h3>
			<p v-if="has_data" class="mgb_5">
				<span class="art_as_of">As of: {{ as_of_text }}</span>
				<span style="float: right;"><b>Currency: $</b></span>
			</p>
		</div>
		<p v-if="!has_data" class="art_no_data">{{ loading ? 'Loading...' : 'No data.' }}</p>
		<div v-else class="art_body">
			<p class="art_period">{{ period_text }}</p>
			<p v-if="result.clamped" class="art_note art_clamped">{{ clamped_notice }}</p>

			<div class="art_kpi_row">
				<div class="art_kpi art_kpi_turnover">
					<div class="art_kpi_label">AR Turnover</div>
					<div class="art_kpi_value">{{ formatTurnover(summary.turnover) }}</div>
				</div>
				<div class="art_kpi art_kpi_collection_days">
					<div class="art_kpi_label">Avg Collection Days</div>
					<div class="art_kpi_value">{{ formatDays(summary.collection_days, ' days') }}</div>
				</div>
				<div class="art_kpi art_kpi_net_sales">
					<div class="art_kpi_label">Net Sales ($)</div>
					<div class="art_kpi_value">{{ summary.net_sales | toCurrency }}</div>
				</div>
				<div class="art_kpi art_kpi_average_ar">
					<div class="art_kpi_label">Average AR ($)</div>
					<div class="art_kpi_value">{{ summary.average_ar | toCurrency }}</div>
				</div>
				<div class="art_kpi art_kpi_current_ar">
					<div class="art_kpi_label">Current AR ($)</div>
					<div class="art_kpi_value">{{ summary.current_ar | toCurrency }}</div>
				</div>
			</div>
			<p v-if="summary.opening_ar !== null" class="art_note art_opening">Includes pre-HERP opening AR of ${{ summary.opening_ar | toCurrency }}.</p>

			<div class="art_chips">
				<button
					v-for="chip in category_chips"
					:key="chip.category"
					type="button"
					:class="['art_chip', chip.css, { art_chip_active: active_category === chip.category }]"
					:aria-pressed="active_category === chip.category ? 'true' : 'false'"
					@click="toggleCategory(chip.category)"
				>
					<span class="art_chip_title"><span :class="['art_dot', chip.css]"></span>{{ chip.chip_label }}</span>
					<span class="art_chip_count">{{ chip.customer_count | commaSeparated }} {{ chip.customer_count === 1 ? 'customer' : 'customers' }}</span>
					<span class="art_chip_amount">${{ chip.current_ar | toCurrency }}</span>
				</button>
				<a v-if="active_category" href="javascript:;" class="art_show_all" @click="clearCategory">Show all</a>
			</div>

			<div class="art_table_wrap">
				<table class="tbl_list art_customers">
					<thead>
						<tr>
							<th v-for="column in columns" :key="column.key" :class="[column.align, 'art_sortable']" :aria-sort="ariaSort(column.key)" @click="sortBy(column.key)">
								<span class="art_th_label">{{ column.label }}</span>
								<span v-if="sort_key === column.key" class="art_sort_mark">{{ sort_dir === 'asc' ? '▲' : '▼' }}</span>
							</th>
						</tr>
					</thead>
					<tbody>
						<tr v-for="row in display_rows" :key="row.row_index">
							<td class="al_left">{{ row.bill_to_code }}</td>
							<td class="al_left">{{ row.bill_to_name }}</td>
							<td class="al_right">{{ row.net_sales | toCurrency }}</td>
							<td class="al_right">{{ row.average_ar | toCurrency }}</td>
							<td class="al_right">{{ row.current_ar | toCurrency }}</td>
							<td class="al_right">{{ formatTurnover(row.turnover) }}</td>
							<td class="al_right">{{ formatDays(row.collection_days, '') }}</td>
							<td class="al_left"><span :class="['art_dot', row.category_css]"></span>{{ row.category_label }}</td>
						</tr>
						<tr v-if="display_rows.length === 0" class="art_empty_row">
							<td :colspan="columns.length" class="al_center">No customers.</td>
						</tr>
					</tbody>
				</table>
			</div>

			<ul class="art_legend">
				<li v-for="(line, index) in legend_lines" :key="index">{{ line }}</li>
			</ul>
		</div>
	</div>
</template>

<script>
import { apiGetArTurnoverArkin } from '@/api/statistics/statistics.js';
import { toCurrency } from '@/utils/etc.js';

// #3367 회전율 구간. 칩과 표의 Category 열이 같은 이름·색을 쓰고, rank 는 Category 열 정렬 순서다.
const CATEGORY_META = {
	HIGH: { label: 'High', chip_label: 'High (≥ 12)', css: 'art_green', rank: 0 },
	MEDIUM: { label: 'Medium', chip_label: 'Medium (6 – <12)', css: 'art_amber', rank: 1 },
	LOW: { label: 'Low', chip_label: 'Low (< 6)', css: 'art_red', rank: 2 },
	NO_SALES: { label: 'No Sales', chip_label: 'No Sales', css: 'art_gray', rank: 3 },
};

const PERIOD_OPTIONS = [
	{ value: 3, label: '3 Months' },
	{ value: 6, label: '6 Months' },
];

const COLUMNS = [
	{ key: 'bill_to_code', label: 'Bill To', type: 'text', align: 'al_left' },
	{ key: 'bill_to_name', label: 'Name', type: 'text', align: 'al_left' },
	{ key: 'net_sales', label: 'Net Sales ($)', type: 'number', align: 'al_right' },
	{ key: 'average_ar', label: 'Average AR ($)', type: 'number', align: 'al_right' },
	{ key: 'current_ar', label: 'Current AR ($)', type: 'number', align: 'al_right' },
	{ key: 'turnover', label: 'AR Turnover', type: 'number', align: 'al_right' },
	{ key: 'collection_days', label: 'Avg Collection Days', type: 'number', align: 'al_right' },
	{ key: 'category', label: 'Category', type: 'category', align: 'al_left' },
];

const LEGEND_LINES = [
	'AR Turnover = Net Sales ÷ Average AR × 365 ÷ days in period. Avg Collection Days = 365 ÷ AR Turnover.',
	'Average AR = average of AR balances on the day before the period, each month-end in the period, and the as-of date.',
	'Net Sales = invoices issued in HERP during the period (opening balances excluded).',
	'Post-dated checks count as collected on their check date.',
	'Past balances are recalculated with payments that are valid today: bounced checks and reversed payments are treated as never received, and waived or voided invoices are excluded, so they may differ from what HERP showed at that time.',
	'Current AR can differ from the AR Aging total because AR Aging keeps unconfirmed post-dated checks and future-dated invoices.',
];

function categoryMeta(category) {
	return CATEGORY_META[category] || { label: category || '', chip_label: category || '', css: 'art_gray', rank: Object.keys(CATEGORY_META).length };
}

function toNumber(value) {
	if (value === null || value === undefined || value === '') return null;
	const number = Number(value);
	return Number.isFinite(number) ? number : null;
}

// #3367 날짜는 서버(회사 시간대) 값을 문자열로만 바꾼다. 브라우저 Date 로 계산하지 않는다. yyyy-MM-dd → MM-dd-yyyy
function toDisplayDate(iso) {
	if (!iso) return '';
	const parts = String(iso)
		.slice(0, 10)
		.split('-');
	if (parts.length !== 3) return String(iso);
	return parts[1] + '-' + parts[2] + '-' + parts[0];
}

export default {
	data: () => ({
		period_months: 3,
		period_options: PERIOD_OPTIONS,
		result: {},
		loading: false,
		request_seq: 0,
		active_category: null,
		sort_key: null,
		sort_dir: 'asc',
		columns: COLUMNS,
		legend_lines: LEGEND_LINES,
	}),
	computed: {
		has_data() {
			return !!(this.result && this.result.summary);
		},
		summary() {
			const summary = this.has_data ? this.result.summary : {};
			return {
				net_sales: toNumber(summary.net_sales),
				average_ar: toNumber(summary.average_ar),
				current_ar: toNumber(summary.current_ar),
				opening_ar: toNumber(summary.opening_ar),
				turnover: toNumber(summary.turnover),
				collection_days: toNumber(summary.collection_days),
				categories: Array.isArray(summary.categories) ? summary.categories : [],
			};
		},
		as_of_text() {
			return toDisplayDate(this.result.as_of);
		},
		period_text() {
			const result = this.result;
			const days_text = result.clamped ? `${result.days} of ${result.nominal_days} days` : `${result.days} days`;
			return `Period: ${toDisplayDate(result.from_date)} – ${toDisplayDate(result.as_of)} (${days_text}, annualized)`;
		},
		clamped_notice() {
			const result = this.result;
			return `Sales history in HERP starts ${toDisplayDate(result.data_start_date)}, so this period is shortened to ${result.days} days. AR before that date is included as opening balances.`;
		},
		category_chips() {
			return this.summary.categories.map(c => {
				const meta = categoryMeta(c.category);
				return {
					category: c.category,
					chip_label: meta.chip_label,
					css: meta.css,
					customer_count: toNumber(c.customer_count) || 0,
					current_ar: toNumber(c.current_ar),
				};
			});
		},
		// row_index 는 서버 순서다. 정렬하지 않았을 때와 같은 값끼리의 순서를 이것으로 지킨다.
		customer_rows() {
			const customers = this.has_data && Array.isArray(this.result.customers) ? this.result.customers : [];
			return customers.map((c, row_index) => {
				const meta = categoryMeta(c.category);
				return {
					row_index,
					bill_to_code: c.bill_to_code || '',
					bill_to_name: c.bill_to_name || '',
					net_sales: toNumber(c.net_sales),
					average_ar: toNumber(c.average_ar),
					current_ar: toNumber(c.current_ar),
					turnover: toNumber(c.turnover),
					collection_days: toNumber(c.collection_days),
					category: c.category,
					category_label: meta.label,
					category_css: meta.css,
					category_rank: meta.rank,
				};
			});
		},
		display_rows() {
			const rows = this.active_category ? this.customer_rows.filter(r => r.category === this.active_category) : this.customer_rows;
			return this.sortRows(rows);
		},
	},
	watch: {
		period_months() {
			this.loadData();
		},
	},
	methods: {
		formatTurnover(value) {
			const number = toNumber(value);
			return number === null ? '—' : toCurrency(number) + 'x';
		},
		formatDays(value, suffix) {
			const number = toNumber(value);
			if (number === null) return '—';
			return number.toFixed(1).replace(/\B(?=(\d{3})+(?!\d))/g, ',') + suffix;
		},
		toggleCategory(category) {
			this.active_category = this.active_category === category ? null : category;
		},
		clearCategory() {
			this.active_category = null;
		},
		sortBy(key) {
			if (this.sort_key === key) {
				this.sort_dir = this.sort_dir === 'asc' ? 'desc' : 'asc';
			} else {
				this.sort_key = key;
				this.sort_dir = 'asc';
			}
		},
		ariaSort(key) {
			if (this.sort_key !== key) return 'none';
			return this.sort_dir === 'asc' ? 'ascending' : 'descending';
		},
		sortValue(row, key) {
			if (key === 'category') return row.category_rank;
			if (key === 'bill_to_code' || key === 'bill_to_name') {
				const text = String(row[key]).trim();
				return text === '' ? null : text;
			}
			return row[key];
		},
		// 값이 없는(null) 행은 오름차순·내림차순 모두 맨 뒤에 둔다. 같은 값은 서버 순서를 유지한다.
		sortRows(rows) {
			if (!this.sort_key) return rows;
			const key = this.sort_key;
			const direction = this.sort_dir === 'desc' ? -1 : 1;
			return rows.slice().sort((a, b) => {
				const a_value = this.sortValue(a, key);
				const b_value = this.sortValue(b, key);
				if (a_value === null || b_value === null) {
					if (a_value === null && b_value === null) return a.row_index - b.row_index;
					return a_value === null ? 1 : -1;
				}
				let compared = 0;
				if (typeof a_value === 'string') compared = a_value.localeCompare(b_value, 'en', { numeric: true, sensitivity: 'base' });
				else compared = a_value < b_value ? -1 : a_value > b_value ? 1 : 0;
				if (compared !== 0) return compared * direction;
				return a.row_index - b.row_index;
			});
		},
		// 기간을 빠르게 바꾸면 늦게 온 이전 응답이 새 결과를 덮지 않게 마지막 요청만 반영한다.
		async loadData() {
			const request_seq = ++this.request_seq;
			this.loading = true;
			try {
				const { data } = await apiGetArTurnoverArkin(this.period_months);
				if (request_seq !== this.request_seq) return;
				this.result = data && typeof data === 'object' && !Array.isArray(data) ? data : {};
			} catch (e) {
				// 오류 안내는 api 인터셉터가 맡는다. 위젯은 No data 로 둔다.
				if (request_seq === this.request_seq) this.result = {};
			} finally {
				if (request_seq === this.request_seq) this.loading = false;
			}
		},
	},
	created() {
		this.loadData();
	},
};
</script>

<style scoped>
/* 공용 .tbl_select 의 width: 100% 를 덮어 제목 오른쪽에 붙인다. */
.art_period_select {
	width: 110px;
	margin-top: 2px;
}
.art_no_data {
	padding: 10px 16px;
	color: #666;
}
.art_body {
	padding: 0 10px;
}
.art_period {
	margin-bottom: 3px;
}
.art_note,
.art_legend {
	color: #666;
	font-size: 12px;
	line-height: 1.5;
}
.art_clamped {
	margin-bottom: 5px;
}
.art_kpi_row {
	display: flex;
	flex-wrap: wrap;
	gap: 8px;
	margin-top: 8px;
}
.art_kpi {
	flex: 1 1 140px;
	padding: 8px 10px;
	border: 1px solid #ddd;
	background: #f5f7fa;
}
.art_kpi_label {
	color: #666;
	font-size: 12px;
}
.art_kpi_value {
	margin-top: 2px;
	font-size: 18px;
	font-weight: bold;
}
.art_opening {
	margin-top: 4px;
}
.art_chips {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 8px;
	margin: 10px 0 8px;
}
.art_chip {
	display: flex;
	flex-direction: column;
	align-items: flex-start;
	min-width: 150px;
	padding: 6px 10px;
	border: 1px solid #ddd;
	border-radius: 6px;
	background: #fff;
	font-size: 12px;
	line-height: 1.5;
	text-align: left;
	cursor: pointer;
}
.art_chip.art_green {
	background: #e8f5e9;
}
.art_chip.art_amber {
	background: #fff8e1;
}
.art_chip.art_red {
	background: #fdecea;
}
.art_chip.art_gray {
	background: #eef0f2;
}
.art_chip.art_chip_active {
	border-color: #333;
	box-shadow: inset 0 0 0 1px #333;
}
.art_chip_title {
	font-weight: bold;
}
.art_show_all {
	font-size: 12px;
	text-decoration: underline;
}
.art_table_wrap {
	max-height: 300px;
	overflow: auto;
}
.tbl_list {
	width: 100%;
	border-collapse: collapse;
}
.tbl_list th,
.tbl_list td {
	border: 1px solid #ddd;
	padding: 6px 8px;
}
.tbl_list thead th {
	position: sticky;
	top: 0;
	z-index: 1;
	background: #f5f7fa;
}
.art_sortable {
	cursor: pointer;
	user-select: none;
}
.art_sort_mark {
	margin-left: 3px;
	font-size: 10px;
}
.art_dot {
	display: inline-block;
	width: 10px;
	height: 10px;
	margin-right: 4px;
	border-radius: 50%;
	vertical-align: middle;
}
.art_dot.art_green {
	background: #2e9e44;
}
.art_dot.art_amber {
	background: #f0ad00;
}
.art_dot.art_red {
	background: #d9363e;
}
.art_dot.art_gray {
	background: #9aa0a6;
}
.art_legend {
	margin-top: 8px;
	padding-left: 16px;
	list-style: disc;
}
</style>
